// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { quickOrderStyles } from "@steiner/Overrides/Widgets/Basic/QuickOrder";
import { color, hideElementOnPrint, xScreen } from "@steiner/XDL/Utilities";
import { css } from "styled-components";

const sharedTextLinkStyle: TypographyPresentationProps = {
    size: 12,
    lineHeight: "15px",
};

quickOrderStyles.container = {
    gap: 0,
    css: css`
        padding: 12px;
        background: ${color("common.ui8")};
        ${hideElementOnPrint}
    `,
};

quickOrderStyles.titleGridItem = {
    width: [12, 6, 6, 6, 6],
};

quickOrderStyles.titleText = {
    variant: "h3",
    color: "common.ui1",
    css: css`
        margin: 0;
    `,
};

quickOrderStyles.linksGridItem = {
    width: [12, 6, 6, 6, 6],
    css: css`
        gap: 16px;
        flex-direction: row-reverse;
        align-items: center;

        ${xScreen("lt", "sm")} {
            justify-content: flex-end;
            flex-wrap: wrap-reverse;
            gap: 12px;

            &&& {
                margin-top: 12px;
            }
        }
        ${xScreen("lte", "xs")} {
            flex-direction: column-reverse;
            justify-content: flex-start;
            align-items: flex-end;
        }
    `,
};

quickOrderStyles.orderUploadLink = {
    typographyProps: {
        ...sharedTextLinkStyle,
    },
    icon: {
        iconProps: {
            src: "Upload",
            size: 13,
            color: "common.ui1",
        },
    },
    css: css`
        ${TypographyStyle} {
            color: ${color("common.ui1")};
        }
    `,
};

quickOrderStyles.orderMultipleItemsLink = {
    typographyProps: {
        ...sharedTextLinkStyle,
    },
    icon: {
        iconProps: {
            src: "Link",
            size: 13,
            color: "common.ui1",
        },
    },
    css: css`
        border-right: 1px solid ${color("common.ui1")};
        padding-right: 16px;

        ${TypographyStyle} {
            color: ${color("common.ui1")};
        }
    `,
};

quickOrderStyles.productSelector = {
    container: {
        gap: 12,
        css: css`
            border: 1px solid ${color("common.border")};
            padding: 12px;
            ${hideElementOnPrint}
        `,
    },
    searchGridItem: {
        width: 12,
    },
    searchDynamicDropdown: {
        iconProps: {
            src: "Search",
        },
        cssOverrides: {
            formInputWrapper: css`
                &&&& ${IconWrapper} {
                    width: 34px;
                    height: 34px;
                    border-radius: 0;
                    top: 0;
                    right: 0;
                    padding: 0;
                    background: ${color("common.ui11")};
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    & svg {
                        stroke: ${color("common.ui6")};
                        width: 20px;
                        height: 20px;
                    }
                }
            `,
            dropdownWrapper: css`
                &&&& input[aria-expanded="true"] ~ ${IconWrapper} {
                    transform: none;
                }
                &&&& input:focus ~ ${IconWrapper} {
                    border: 1px solid ${color("common.ui10")};
                    border-left-width: 0;
                }
            `,
        },
    },
    unitOfMeasureGridItem: {
        width: 0,
        css: css`
            display: none;
        `,
    },
    actionsGridItem: {
        width: 12,
        css: css`
            gap: 12px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
        `,
    },
    clearIcon: {
        size: 20,
        css: css`
            right: 45px;
            top: 35px;
            cursor: pointer;
        `,
    },
    selectButton: {
        sizeVariant: "large",
        css: css`
            width: 100%;
            max-width: 230px;

            ${xScreen("lte", "xs")} {
                max-width: 100%;
            }
        `,
    },
};
